import { getBearing, getBearing360, getDistance, getRadialCoordinates } from "../../utils/gis-helpers";
import { getMeanTime } from "src/app/module/aero-navic/aero-navic.module";
import { TimeConverterPipe } from "src/app/pipes/time-converter/time-converter.pipe";
import { MapCenterEvent } from "./webgl-maplibre.types";

export function computeCursorInfo(
  target: number[],
  aircraftPos: number[],
  isCentered: boolean
): Omit<MapCenterEvent, "elevation"> {
  const heading = Math.round(getBearing360(getBearing(aircraftPos, target)));
  const distanceRaw = getDistance(target, aircraftPos);
  const time = new TimeConverterPipe().transform(getMeanTime(distanceRaw, 120));

  return {
    isCentered,
    center: target,
    time,
    distanceRaw,
    heading,
    info: null,
  };
}

export function buildAircraftNavFC(geoInfo: {
  longitude: number;
  latitude: number;
  heading: number;
  speed: number;
}): GeoJSON.FeatureCollection {
  const center: [number, number] = [geoInfo.longitude, geoInfo.latitude];
  const heading = geoInfo.heading;
  const speed = geoInfo.speed;
  const oneMinDistance = (speed * 60) / 1852;

  const features: GeoJSON.Feature[] = [];

  // Aircraft point with heading for icon rotation
  features.push({
    type: "Feature",
    properties: { heading },
    geometry: { type: "Point", coordinates: center },
  });

  // Heading line: aircraft → 2× one-minute distance ahead
  const aheadPoint = getRadialCoordinates(center, heading, oneMinDistance * 2);
  features.push({
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: [center, aheadPoint] },
  });

  // Crossbar: perpendicular line at one-minute distance
  const crossLeft = getRadialCoordinates(center, heading - 3, oneMinDistance);
  const crossRight = getRadialCoordinates(center, heading + 3, oneMinDistance);
  features.push({
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: [crossLeft, crossRight] },
  });

  return { type: "FeatureCollection", features };
}

/** Remove consecutive duplicate coordinates to avoid zero-length line segments. */
export function deduplicateCoords(coords: number[][]): number[][] {
  if (coords.length === 0) return coords;
  const result = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    if (coords[i][0] !== coords[i - 1][0] || coords[i][1] !== coords[i - 1][1]) {
      result.push(coords[i]);
    }
  }
  return result;
}
